import { Filter, FilterProps } from "@/models/Pageable"

import type { AiContextType } from "./AI"
import type { Picture } from "./User"

export type AiActivityOriginator = "USER" | "SYSTEM"
export type AiActivityStatus = "SUCCESS" | "FAILED"
export type AiActivityFeedback = "GOOD" | "BAD" | "NO_FEEDBACK"
export type AiProviderType = "OPEN_AI" | "GEMINI" | "GROK" | "DEEPSEEK" | "CLAUDE"
export type AiActivityDateRange =
  | "TODAY"
  | "LAST_DAY"
  | "LAST_3_DAYS"
  | "LAST_7_DAYS"
  | "LAST_30_DAYS"
  | "LAST_90_DAYS"
  | "LAST_YEAR"

export interface AiRequestMessage {
  id: string
  role: "system" | "user" | "assistant" | string
  content: string
}

export class AiRequestLogPreview {
  id = ""
  title = ""
  contextType: AiContextType
  originator: AiActivityOriginator
  userId = ""
  projectId = ""
  projectName = ""
  projectUrl = ""
  accountId = ""
  accountName = ""
  accountUrl = ""
  accountPicture?: Picture | null
  provider: AiProviderType
  model = ""
  status: AiActivityStatus
  durationMs?: number
  goodCount = 0
  badCount = 0
  createdDate = ""

  constructor(props?: Partial<AiRequestLogPreview>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export interface AiRequestLogDetail extends AiRequestLogPreview {
  requestMessages: AiRequestMessage[]
  response: string | null
  errorMessage: string | null
  temperature: number | null
  maxTokens: number | null
  isDefaultTokenUsed: boolean
  contextEntityId: string | null
}

export class AiRequestStats {
  totalCount = 0
  failedCount = 0
  successRate?: number
  avgDurationMs?: number
  goodFeedbackCount = 0
  badFeedbackCount = 0
  noFeedbackCount = 0
  countByContextType: Partial<Record<AiContextType, number>> = {}
  totalCountChangePct?: number
  failedCountChangePct?: number
  successRateChangePct?: number
  avgDurationChangePct?: number

  constructor(props?: Partial<AiRequestStats>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

interface FilterAiActivityLogProps extends FilterProps {
  contextTypes?: AiContextType[]
  originator?: AiActivityOriginator
  projectId?: string
  status?: AiActivityStatus
  feedback?: AiActivityFeedback
  dateRange?: AiActivityDateRange
  from?: string
  to?: string
  accountId?: string
}

/**
 * Serializes AI request filters according to the shared list/stats backend contract.
 * Account filtering is emitted only for the super-admin endpoint.
 */
export class FilterAiActivityLog extends Filter {
  contextTypes: AiContextType[] = []
  originator?: AiActivityOriginator
  projectId = ""
  status?: AiActivityStatus
  feedback?: AiActivityFeedback
  dateRange?: AiActivityDateRange
  from = ""
  to = ""
  accountId = ""

  constructor(props: FilterAiActivityLogProps) {
    super(props)
    this.contextTypes = props.contextTypes ?? []
    this.originator = props.originator
    this.projectId = props.projectId ?? ""
    this.status = props.status
    this.feedback = props.feedback
    this.dateRange = props.dateRange
    this.from = props.from ?? ""
    this.to = props.to ?? ""
    this.accountId = props.accountId ?? ""
  }

  private getParams(includeAccountId: boolean) {
    return {
      contextTypes: this.contextTypes.length ? this.contextTypes.join(",") : undefined,
      originator: this.originator,
      projectId: this.projectId || undefined,
      status: this.status,
      feedback: this.feedback,
      dateRange: this.dateRange,
      from: this.dateRange ? undefined : this.from || undefined,
      to: this.dateRange ? undefined : this.to || undefined,
      accountId: includeAccountId ? this.accountId || undefined : undefined
    }
  }

  getActivityLogJsonObj(includeAccountId = false) {
    return super.getJsonObj({ params: this.getParams(includeAccountId) })
  }

  getStatsJsonObj(includeAccountId = false) {
    return super.getJsonObj({
      params: this.getParams(includeAccountId),
      withoutPagination: true
    })
  }

  static createDefault() {
    return new FilterAiActivityLog({
      page: 1,
      size: 20,
      sort: []
    })
  }
}
